import React from 'react';

/**
 * Roadmap Section
 *
 * Implementation phases with effort/value analysis.
 * Designed for CEO presentation with clear business value.
 */
const RoadmapSection = ({ content }) => {
  return (
    <div className="df-roadmap-section">
      <header className="df-section__header">
        <h2 className="df-section__title">{content.title}</h2>
        <p className="df-section__subtitle">{content.subtitle}</p>
      </header>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <LegendGroup title="Effort" items={content.effortLegend} />
        <LegendGroup title="Value" items={content.valueLegend} />
      </div>

      {/* Phase Cards */}
      <div className="df-roadmap">
        {content.phases.map((phase) => (
          <PhaseCard key={phase.id} phase={phase} />
        ))}
      </div>
    </div>
  );
};

/**
 * Legend Group Component
 */
const LegendGroup = ({ title, items }) => {
  return (
    <div>
      <span style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </span>
      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
        {Object.entries(items).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '3px',
                background: value.color
              }}
            />
            <span style={{ fontSize: '0.8rem', color: '#475569' }}>{key}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Phase Card Component
 */
const PhaseCard = ({ phase }) => {
  const effortClass = `df-phase-card__badge--effort-${phase.effort.toLowerCase()}`;
  const valueClass = `df-phase-card__badge--value-${phase.value.toLowerCase().replace(' ', '-')}`;

  return (
    <div className={`df-phase-card df-phase-card--${phase.status}`}>
      <div className="df-phase-card__header">
        <div className="df-phase-card__title-group">
          <span className="df-phase-card__number">Phase {phase.id}</span>
          <h3 className="df-phase-card__title">{phase.title}</h3>
          {phase.status === 'current' && (
            <StatusBadge status="current" />
          )}
        </div>
        <div className="df-phase-card__badges">
          <span className={`df-phase-card__badge ${effortClass}`}>
            Effort: {phase.effort}
          </span>
          <span className={`df-phase-card__badge ${valueClass}`}>
            Value: {phase.value}
          </span>
        </div>
      </div>

      <p className="df-phase-card__description">{phase.description}</p>

      <ul className="df-phase-card__deliverables">
        {phase.deliverables.map((item, index) => (
          <li key={index} className="df-phase-card__deliverable">
            {item}
          </li>
        ))}
      </ul>

      <div className="df-phase-card__value">
        <strong>Business Value:</strong> {phase.businessValue}
      </div>

      {phase.dependencies.length > 0 && (
        <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#64748B' }}>
          <DependencyIcon /> Depends on Phase {phase.dependencies.join(', ')}
        </div>
      )}
    </div>
  );
};

/**
 * Status Badge Component
 */
const StatusBadge = ({ status }) => {
  const styles = {
    current: {
      background: 'rgba(31, 147, 254, 0.15)',
      color: '#1F93FE',
      border: '1px solid rgba(31, 147, 254, 0.3)'
    },
    completed: {
      background: 'rgba(16, 185, 129, 0.15)',
      color: '#10b981',
      border: '1px solid rgba(16, 185, 129, 0.3)'
    },
    planned: {
      background: 'rgba(100, 116, 139, 0.15)',
      color: '#64748B',
      border: '1px solid rgba(100, 116, 139, 0.3)'
    }
  };

  const labels = {
    current: 'In Progress',
    completed: 'Completed',
    planned: 'Planned'
  };

  return (
    <span
      style={{
        ...styles[status],
        fontSize: '0.7rem',
        padding: '0.2rem 0.5rem',
        borderRadius: '4px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem'
      }}
    >
      {status === 'current' && <PulsingDot />}
      {labels[status]}
    </span>
  );
};

/**
 * Pulsing Dot for current status
 */
const PulsingDot = () => (
  <span
    style={{
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: '#1F93FE',
      animation: 'pulse 2s infinite'
    }}
  />
);

/**
 * Dependency Icon
 */
const DependencyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    width="14"
    height="14"
    style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.3rem' }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// Add pulse animation via CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('df-pulse-style')) {
  style.id = 'df-pulse-style';
  document.head.appendChild(style);
}

export default RoadmapSection;
